import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClientPlatform from '@/apiClientPlatform'
import type {
  ManualMessagingPaidBody,
  ManualMessagingPaidResponse,
  ManualMessagingSuspendBody,
  ManualMessagingSuspendResponse,
  ManualPlanBody,
  ManualPlanResponse,
  ManualTopupBody,
  ManualTopupResponse,
  PlatformOwnerDetailResponse,
} from '@/types/platform'

export const usePlatformOwnerDetailQuery = (ownerId: string) => {
  return useQuery({
    queryKey: ['platformOwner', ownerId],
    enabled: !!ownerId,
    queryFn: async () => {
      const { data } = await apiClientPlatform.get<PlatformOwnerDetailResponse>(
        `/platform/owners/${ownerId}`
      )
      return data
    },
  })
}

export const useManualPlanMutation = (ownerId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ManualPlanBody) => {
      const { data } = await apiClientPlatform.post<ManualPlanResponse>(
        `/platform/billing/owners/${ownerId}/plan/manual`,
        payload
      )
      return data
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['platformOwner', ownerId] })
      await qc.invalidateQueries({
        queryKey: ['platformOwnerOverview', ownerId],
      })
      await qc.invalidateQueries({ queryKey: ['platformAudit'] })
      await qc.invalidateQueries({ queryKey: ['platformOwners'] })
    },
  })
}

export const useManualTopupMutation = (ownerId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ManualTopupBody) => {
      const { data } = await apiClientPlatform.post<ManualTopupResponse>(
        `/platform/billing/owners/${ownerId}/messages/topup`,
        payload
      )
      return data
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['platformOwner', ownerId] })
      await qc.invalidateQueries({
        queryKey: ['platformOwnerOverview', ownerId],
      })
      await qc.invalidateQueries({ queryKey: ['platformAudit'] })
      await qc.invalidateQueries({ queryKey: ['platformOwners'] })
    },
  })
}

export const useManualMessagingPaidMutation = (ownerId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ManualMessagingPaidBody) => {
      const { data } =
        await apiClientPlatform.post<ManualMessagingPaidResponse>(
          `/platform/billing/owners/${ownerId}/messaging/mark-paid`,
          payload
        )
      return data
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['platformOwner', ownerId] })
      await qc.invalidateQueries({
        queryKey: ['platformOwnerOverview', ownerId],
      })
      await qc.invalidateQueries({ queryKey: ['platformAudit'] })
      await qc.invalidateQueries({ queryKey: ['platformOwners'] })
    },
  })
}

export const useManualMessagingSuspendMutation = (ownerId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ManualMessagingSuspendBody) => {
      const { data } =
        await apiClientPlatform.post<ManualMessagingSuspendResponse>(
          `/platform/billing/owners/${ownerId}/messaging/suspend`,
          payload
        )
      return data
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['platformOwner', ownerId] })
      await qc.invalidateQueries({
        queryKey: ['platformOwnerOverview', ownerId],
      })
      await qc.invalidateQueries({ queryKey: ['platformAudit'] })
      await qc.invalidateQueries({ queryKey: ['platformOwners'] })
    },
  })
}

export const usePlatformOwnerSubscriptionPaymentsQuery = (
  ownerId: string,
  take = 20
) =>
  useQuery({
    enabled: !!ownerId,
    queryKey: ['platformOwnerSubscriptionPayments', ownerId, take],
    queryFn: async () => {
      const { data } = await apiClientPlatform.get(
        `/platform/billing/owners/${ownerId}/subscription-payments`,
        { params: { take } }
      )
      return data
    },
  })

export const usePlatformOwnerMessagingSubscriptionPaymentsQuery = (
  ownerId: string,
  take = 20
) =>
  useQuery({
    enabled: !!ownerId,
    queryKey: ['platformOwnerMessagingSubscriptionPayments', ownerId, take],
    queryFn: async () => {
      const { data } = await apiClientPlatform.get(
        `/platform/owners/${ownerId}/billing/messaging-subscription-payments`,
        { params: { take } }
      )
      return data
    },
  })

export const usePlatformOwnerMessageCreditsQuery = (
  ownerId: string,
  take = 20
) =>
  useQuery({
    enabled: !!ownerId,
    queryKey: ['platformOwnerMessageCredits', ownerId, take],
    queryFn: async () => {
      const { data } = await apiClientPlatform.get(
        `/platform/owners/${ownerId}/billing/message-credits`,
        { params: { take } }
      )
      return data
    },
  })

export function usePendingOwnerSubscriptionPayments(ownerId: string) {
  return useQuery({
    queryKey: ['platformOwnerSubscriptionPayments', ownerId],
    queryFn: async () => {
      const { data } = await apiClientPlatform.get(
        `/platform/billing/owners/${ownerId}/subscription-payments`
      )
      return Array.isArray(data) ? data : data?.items ?? []
    },
    enabled: !!ownerId,
  })
}

export function useApproveSubscriptionPaymentMutation(ownerId: string) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (paymentId: string) => {
      const { data } = await apiClientPlatform.post(
        `/platform/billing/subscription-payments/${paymentId}/approve`
      )
      return data
    },
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: ['platformOwnerSubscriptionPayments', ownerId],
      })
      await qc.invalidateQueries({
        queryKey: ['platformOwner', ownerId],
      })
      await qc.invalidateQueries({
        queryKey: ['platformOwnerOverview', ownerId],
      })
      await qc.invalidateQueries({
        queryKey: ['platformOwners'],
      })
      await qc.invalidateQueries({
        queryKey: ['platformAudit'],
      })
    },
  })
}
